import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, signInWithTimeout } from '../lib/supabase';
import api from '../lib/api';
import { trackPixel, reportRegistration } from '../lib/pixel';
import { phoneToAuthEmail, looksLikePhone } from '../lib/phoneAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Briefcase, User, ArrowLeft, Home } from 'lucide-react';
import { BOHOL_TOWNS, WORKER_SKILLS } from '../lib/constants';
import { SkillsSelector } from '../components/profile';
import { AnonymousResumeUploadCard } from '../components/AnonymousResumeUploadCard';
import { uploadResume } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { takeReturnTo } from '../lib/returnTo';

// Facebook icon (defined outside component)
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Social Login Icons (defined outside component)
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Divider component (defined outside)
const OrDivider = ({ text }) => (
  <div className="relative my-4">
    <div className="absolute inset-0 flex items-center">
      <span className="w-full border-t" />
    </div>
    <div className="relative flex justify-center text-xs uppercase">
      <span className="bg-background px-2 text-muted-foreground">
        {text}
      </span>
    </div>
  </div>
);

// Helper to detect service outage errors (Supabase down, network failure, etc.)
const isServiceError = (error) => {
  const msg = (error?.message || '').toLowerCase();
  return msg.includes('fetch') || msg.includes('network') ||
    msg.includes('502') || msg.includes('503') || msg.includes('522') ||
    msg.includes('connection') || msg.includes('failed to fetch') ||
    msg.includes('<!doctype') || msg.includes('<html') ||
    error?.name === 'TypeError' || error?.status >= 500;
};

const SERVICE_DOWN_MSG = 'Our servers are temporarily unavailable. Please try again in a few minutes.';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [referralCode, setReferralCode] = useState(null);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [referralCodeValid, setReferralCodeValid] = useState(null); // null = unchecked, true/false
  const [referrerName, setReferrerName] = useState('');
  const [logoUrl, setLogoUrl] = useState('/logo-transparent.png');
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false); // Flag to prevent race condition
  const [termsAgreed, setTermsAgreed] = useState(false); // Privacy & Terms agreement
  const [showFacebookLogin, setShowFacebookLogin] = useState(false);

  // Sign In State
  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });

  // Resume file held in memory during signup so it can be uploaded to the
  // user's profile AFTER the account is created (anonymous parse doesn't store).
  const [pendingResumeFile, setPendingResumeFile] = useState(null);
  // Full extracted JSON from the resume parse, held so we can apply fields
  // the signup form doesn't capture (education_field, tagline, work_history)
  // via PUT /api/profile after the account is created.
  const [pendingResumeExtracted, setPendingResumeExtracted] = useState(null);

  // Sign Up State
  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'job_seeker',
    town: '',
    // Job seeker-specific fields
    phone: '',
    years_of_experience: '',
    education_level: '',
    date_of_birth: '',
    skills: [],
    // Employer-specific fields
    is_private_household: false,
    business_name: '',
    business_type: '',
    industry: '',
    business_description: '',
    business_phone: '',
    employee_count: ''
  });

  const checkSession = useCallback(async () => {
    // Skip if we're in the middle of signing up (prevents race condition)
    if (isSigningUp) {
      console.log('[Auth] Skipping checkSession - signup in progress');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Check if user has completed role selection
        // Use auth_user_id to find profile (not id, which is the profile's own UUID)
        // Use maybeSingle to handle edge cases gracefully (no profile, or profile not found)
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (!profile?.role || profile.role === 'pending') {
          // User needs to select role - use full reload to ensure clean state
          window.location.href = '/role-selection';
        } else {
          // Stored, not router state: /auth -> /sign-up forwards only location.search,
          // and this is a full reload, so router state never survives the trip.
          const returnTo = takeReturnTo() || '/dashboard';
          // Use full reload to ensure App.js properly initializes
          window.location.href = returnTo;
        }
      }
    } catch (error) {
      // Non-fatal - user can still use the auth page
      console.error('[Auth] checkSession error:', error);
    }
  }, [isSigningUp]);

  useEffect(() => {
    checkSession();

    // Listen for auth state changes (important for PWA returning from external OAuth)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        checkSession();
      }
    });

    // Also check session when window regains focus (user returning from browser)
    const handleFocus = () => {
      checkSession();
    };
    window.addEventListener('focus', handleFocus);

    // Fetch logo
    const fetchLogo = async () => {
      try {
        const response = await api.get('/api/settings/version');
        if (response.data.logo_url) {
          setLogoUrl(response.data.logo_url);
          try { localStorage.setItem('app_logo_url', response.data.logo_url); } catch {}
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    fetchLogo();

    // Show Facebook login for all users
    setShowFacebookLogin(true);

    // Capture referral code from URL and persist it
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      setReferralCodeInput(ref.toUpperCase());
      try { localStorage.setItem('referral_code', ref); } catch {}
      console.log('Referral code captured and stored:', ref);
      // Validate the URL referral code
      api.get(`/api/referrals/validate/${encodeURIComponent(ref)}`)
        .then(res => {
          if (res.data?.valid) {
            setReferralCodeValid(true);
            setReferrerName(res.data.referrer_name || '');
          }
        })
        .catch(() => {});
    } else {
      try {
        const storedRef = localStorage.getItem('referral_code');
        if (storedRef) {
          setReferralCode(storedRef);
          setReferralCodeInput(storedRef.toUpperCase());
          console.log('Referral code restored from storage:', storedRef);
        }
      } catch {}
    }

    // Cleanup
    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkSession, searchParams]);

  // Check if running in PWA standalone mode
  // Social Login Handlers
  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    try {
      // Carry referral code through OAuth redirect so it survives even if localStorage is cleared
      let storedRef = null;
      try { storedRef = localStorage.getItem('referral_code'); } catch {}
      const callbackRef = referralCode || storedRef;
      const callbackUrl = callbackRef
        ? `${window.location.origin}/auth/callback?ref=${encodeURIComponent(callbackRef)}`
        : `${window.location.origin}/auth/callback`;

      // In PWA standalone mode, open OAuth in a new browser window
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
      // User will be redirected to Google
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(isServiceError(error) ? SERVICE_DOWN_MSG : (error.message || 'Failed to login with Google'));
      setSocialLoading('');
    }
  };

  const handleFacebookLogin = async () => {
    setSocialLoading('facebook');
    try {
      // Carry referral code through OAuth redirect so it survives even if localStorage is cleared
      let storedRef = null;
      try { storedRef = localStorage.getItem('referral_code'); } catch {}
      const callbackRef = referralCode || storedRef;
      const callbackUrl = callbackRef
        ? `${window.location.origin}/auth/callback?ref=${encodeURIComponent(callbackRef)}`
        : `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: callbackUrl,
          scopes: 'email,public_profile'
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Facebook login error:', error);
      toast.error(isServiceError(error) ? SERVICE_DOWN_MSG : (error.message || 'Failed to login with Facebook'));
      setSocialLoading('');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Accept EITHER an email or a phone number. Phone-only seekers (no email) log in
      // with their phone, which maps to the same derived handle used at signup. Anything
      // containing "@" is treated as an email and passes through unchanged.
      const identifier = signInData.email.trim();
      const loginEmail = looksLikePhone(identifier) ? phoneToAuthEmail(identifier) : identifier;
      // Use timeout wrapper to prevent mobile hangs (15 second timeout)
      const { error } = await signInWithTimeout(
        loginEmail,
        signInData.password,
        15000
      );

      if (error) throw error;

      toast.success(t('messages.loginSuccess'));

      // Use full page reload to ensure App.js properly initializes with fresh session
      // This prevents "authenticated but no profile" issues where React state is stale
      const returnTo = takeReturnTo() || '/dashboard';
      window.location.href = returnTo;
    } catch (error) {
      console.error('[Auth] Sign in error:', error);
      toast.error(isServiceError(error) ? SERVICE_DOWN_MSG : (error.message || 'Failed to sign in. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Require terms agreement
    if (!termsAgreed) {
      toast.error(t('auth.pleaseAgreeToTerms', 'Please agree to the Privacy Policy and Terms of Service'));
      return;
    }

    if (signUpData.password !== signUpData.confirmPassword) {
      toast.error(t('messages.passwordMismatch') || 'Passwords do not match');
      return;
    }

    if (signUpData.password.length < 6) {
      toast.error(t('messages.passwordTooShort') || 'Password must be at least 6 characters');
      return;
    }

    if (!signUpData.town) {
      toast.error(t('messages.selectTown') || 'Please select your town');
      return;
    }

    setLoading(true);
    setIsSigningUp(true); // Prevent race condition with onAuthStateChange

    // Validation for job seeker fields
    if (signUpData.role === 'job_seeker') {
      if (!signUpData.phone || signUpData.phone.trim() === '') {
        toast.error(t('messages.phoneRequired') || 'Phone number is required');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      if (!signUpData.years_of_experience) {
        toast.error(t('messages.experienceRequired') || 'Please select your years of experience');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      if (!signUpData.education_level) {
        toast.error(t('messages.educationRequired') || 'Please select your education level');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      if (!signUpData.date_of_birth) {
        toast.error(t('messages.dobRequired') || 'Date of birth is required');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      // Minimum age check (16 years old)
      const dob = new Date(signUpData.date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 16) {
        toast.error('You must be at least 16 years old to register');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      if (!signUpData.skills || signUpData.skills.length === 0) {
        toast.error(t('messages.skillsRequired', 'Please select your skills before proceeding. Tap on the skills that match your experience.'));
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
    }

    // Validation for employer fields
    if (signUpData.role === 'employer') {
      if (!signUpData.business_phone || signUpData.business_phone.trim() === '') {
        toast.error(t('messages.phoneRequired') || 'Phone number is required');
        setLoading(false);
        setIsSigningUp(false);
        return;
      }
      // Private households skip business-specific validation
      if (!signUpData.is_private_household) {
        if (!signUpData.business_name) {
          toast.error(t('messages.businessNameRequired') || 'Please enter your business name');
          setLoading(false);
          setIsSigningUp(false);
          return;
        }
        if (!signUpData.industry) {
          toast.error(t('messages.industryRequired') || 'Please select your industry');
          setLoading(false);
          setIsSigningUp(false);
          return;
        }
        if (!signUpData.employee_count) {
          toast.error(t('messages.employeeCountRequired') || 'Please select number of employees');
          setLoading(false);
          setIsSigningUp(false);
          return;
        }
      }
    }

    try {
      // For private households, auto-generate the business name from town
      const isHousehold = signUpData.role === 'employer' && signUpData.is_private_household;
      const effectiveBusinessName = isHousehold
        ? `Private Household in ${signUpData.town || 'Bohol'}`
        : signUpData.business_name;

      const userData = {
        name: signUpData.role === 'employer' ? (effectiveBusinessName || 'Employer') : signUpData.name,
        role: signUpData.role,
        town: signUpData.town,
        terms_agreed_at: new Date().toISOString(), // Record that user agreed during registration
      };

      let storedRefCode = null;
      try { storedRefCode = localStorage.getItem('referral_code'); } catch {}
      const typedCode = referralCodeInput.trim().toUpperCase();
      const finalReferralCode = typedCode || referralCode || storedRefCode;

      if (finalReferralCode) {
        userData.referred_by = finalReferralCode;
        console.log('REFERRAL: Adding to userData:', finalReferralCode);
      }

      if (signUpData.role === 'job_seeker') {
        userData.phone = signUpData.phone;
        userData.years_of_experience = signUpData.years_of_experience;
        userData.education_level = signUpData.education_level;
        userData.date_of_birth = signUpData.date_of_birth;
        userData.skills = signUpData.skills;
      }

      if (signUpData.role === 'employer') {
        userData.phone = signUpData.business_phone;
        userData.business_name = effectiveBusinessName;
        userData.business_phone = signUpData.business_phone;
        userData.is_private_household = isHousehold;
        if (isHousehold) {
          userData.industry = 'Private Household';
          userData.business_type = 'Private Household';
          userData.employee_count = '1-5';
        } else {
          userData.business_type = signUpData.business_type;
          userData.industry = signUpData.industry;
          userData.business_description = signUpData.business_description;
          userData.employee_count = signUpData.employee_count;
        }
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (authError) throw authError;

      // Meta Pixel conversion: a new account was created (no-ops until cookies are accepted).
      // The shared reg-<auth user id> event id lets Meta dedup this against the server-side
      // copy sent below (or from AuthCallback once the email is verified).
      // Supabase answers an already-registered email with an obfuscated user that has no
      // identities — that is not a new account, so don't count it.
      const newUserId = authData?.user?.id || authData?.session?.user?.id;
      const isExistingEmail = Array.isArray(authData?.user?.identities) && authData.user.identities.length === 0;
      if (!isExistingEmail) {
        trackPixel('CompleteRegistration', { content_name: signUpData.role || 'user' },
                   newUserId ? `reg-${newUserId}` : undefined);
      }

      try { localStorage.removeItem('referral_code'); } catch {}

      // Check if user is immediately logged in (email verification disabled)
      if (authData?.session) {
        console.log('[Auth] User logged in immediately (no email verification)');

        // Try to find existing profile
        // Use maybeSingle to handle edge cases gracefully
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id')
          .eq('auth_user_id', authData.session.user.id)
          .maybeSingle();

        if (profileData?.id) {
          // Profile exists, update it with signup data
          console.log('[Auth] Profile found, updating with signup data');
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              ...userData,
              updated_at: new Date().toISOString()
            })
            .eq('id', profileData.id);

          if (updateError) {
            console.error('[Auth] Error updating profile:', updateError);
            // Non-fatal - profile exists, user can update later
          }
        } else {
          // Profile doesn't exist yet - trigger may still be creating it
          // Wait briefly and retry before attempting to create
          console.log('[Auth] Profile not found, waiting for trigger...');
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Retry fetch
          const { data: retryProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('auth_user_id', authData.session.user.id)
            .maybeSingle();

          if (retryProfile?.id) {
            // Profile was created by trigger, update it
            console.log('[Auth] Profile found on retry, updating');
            await supabase
              .from('profiles')
              .update({
                ...userData,
                updated_at: new Date().toISOString()
              })
              .eq('id', retryProfile.id);
          } else {
            // Profile still doesn't exist, create with UPSERT to handle race conditions
            console.log('[Auth] Creating profile with upsert');
            const { error: createError } = await supabase
              .from('profiles')
              .upsert({
                id: authData.session.user.id,
                auth_user_id: authData.session.user.id,
                email: authData.session.user.email,
                ...userData,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }, {
                onConflict: 'id',
                ignoreDuplicates: false
              });

            if (createError) {
              console.error('[Auth] Error creating profile:', createError);
              toast.error('Failed to create profile. Please try again.');
              setIsSigningUp(false);
              return;
            }
          }
        }

        // If the user uploaded a resume during signup (anonymous parse), now that
        // they're authenticated, save the file to their profile + apply any extra
        // parsed fields the signup form didn't capture. Both best-effort —
        // don't block signup if either fails.
        if (pendingResumeFile) {
          try {
            await uploadResume(pendingResumeFile);
            console.log('[Auth] Resume file saved to profile after signup');
          } catch (uploadErr) {
            console.warn('[Auth] Failed to save resume file (non-fatal):', uploadErr);
          }
        }
        if (pendingResumeExtracted) {
          // Apply extras: fields the signup form doesn't have but PUT /api/profile accepts.
          // (name/phone/town/years_of_experience/education_level/skills already saved via signup.)
          const extras = {};
          const x = pendingResumeExtracted;
          if (x.education_field) extras.education_field = x.education_field;
          if (x.tagline && x.tagline.length <= 80) extras.tagline = x.tagline;
          if (Array.isArray(x.work_history) && x.work_history.length > 0) {
            extras.work_history = x.work_history.slice(0, 5);
          }
          if (Object.keys(extras).length > 0) {
            try {
              const { data: sess } = await supabase.auth.getSession();
              const token = sess?.session?.access_token;
              if (token) {
                await api.put('/api/profile', extras, { headers: { Authorization: `Bearer ${token}` } });
                console.log('[Auth] Resume extras applied to profile:', Object.keys(extras));
              }
            } catch (putErr) {
              console.warn('[Auth] Failed to apply resume extras (non-fatal):', putErr);
            }
          }
        }

        // Server-side mirror of the sign-up (Conversions API) — reaches Meta even when the
        // visitor declined cookies or the pixel is blocked. Pixel already fired above.
        await reportRegistration({
          token: authData.session.access_token,
          userId: authData.session.user.id,
          role: signUpData.role,
          pixel: false,
        });

        toast.success(t('messages.accountCreated') || 'Account created successfully!');
        setIsSigningUp(false); // Reset flag before navigation
        // Use full page reload to ensure App.js properly initializes with fresh session.
        // Send them back to the job they were applying for — signing up is an
        // interruption, not the destination.
        window.location.href = takeReturnTo() || '/dashboard';
      } else {
        // Email verification required
        setIsSigningUp(false); // Reset flag before navigation
        window.location.href = `/check-email?email=${encodeURIComponent(signUpData.email)}`;
      }
    } catch (error) {
      toast.error(isServiceError(error) ? SERVICE_DOWN_MSG : error.message);
      setIsSigningUp(false); // Reset on error
    } finally {
      setLoading(false);
    }
  };

  // Render Login Form (default view)
  const renderLoginForm = () => (
    <div className="space-y-4">
      {/* Google Sign In Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full h-12 text-base font-medium"
        onClick={handleGoogleLogin}
        disabled={socialLoading !== ''}
      >
        {socialLoading === 'google' ? (
          <div className="animate-spin mr-2 h-5 w-5 border-2 border-gray-300 border-t-primary rounded-full" />
        ) : (
          <GoogleIcon />
        )}
        {t('auth.continueWithGoogle', 'Continue with Google')}
      </Button>

      {/* Facebook Sign In Button */}
      {showFacebookLogin && (
        <Button
          type="button"
          variant="outline"
          className="w-full h-12 text-base font-medium"
          onClick={handleFacebookLogin}
          disabled={socialLoading !== ''}
        >
          {socialLoading === 'facebook' ? (
            <div className="animate-spin mr-2 h-5 w-5 border-2 border-gray-300 border-t-[#1877F2] rounded-full" />
          ) : (
            <FacebookIcon />
          )}
          {t('auth.signInWithFacebook', 'Continue with Facebook')}
        </Button>
      )}

      <OrDivider text={t('auth.orContinueWith', 'or')} />

      {/* Email Sign In Form */}
      <form onSubmit={handleSignIn}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signin-email">{t('auth.emailOrPhone', 'Email or phone number')}</Label>
            <Input
              id="signin-email"
              type="text"
              placeholder={t('auth.emailOrPhonePlaceholder', 'you@example.com or 09XX XXX XXXX')}
              value={signInData.email}
              onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signin-password">{t('auth.password')}</Label>
            <Input
              id="signin-password"
              type="password"
              value={signInData.password}
              onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
              autoComplete="current-password"
              required
            />
          </div>
          <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading}>
            {loading ? t('common.loading') : t('auth.signInButton', 'Log In')}
          </Button>
          <Button
            type="button"
            variant="link"
            onClick={() => navigate('/forgot-password')}
            className="w-full text-sm"
          >
            {t('auth.forgotPassword', 'Forgot password?')}
          </Button>
        </div>
      </form>

      <OrDivider text="" />

      {/* Create New Account Button */}
      <Button
        type="button"
        variant="default"
        className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
        onClick={() => setShowSignUpForm(true)}
      >
        {t('auth.createNewAccount', 'Create new account')}
      </Button>
    </div>
  );

  // Render Sign Up Form
  const renderSignUpForm = () => (
    <div className="space-y-4">
      {/* Back Button */}
      <Button
        type="button"
        variant="ghost"
        className="w-full mb-2"
        onClick={() => {
          setShowSignUpForm(false);
          setTermsAgreed(false); // Reset terms agreement when going back
        }}
      >
        <ArrowLeft size={18} className="mr-2" />
        {t('auth.backToLogin', 'Back to Login')}
      </Button>

      {/* Google Sign Up Button - terms agreement handled on ProfileCompletion page after OAuth */}
      <Button
        type="button"
        variant="outline"
        className="w-full h-12 text-base font-medium"
        onClick={handleGoogleLogin}
        disabled={socialLoading !== ''}
      >
        {socialLoading === 'google' ? (
          <div className="animate-spin mr-2 h-5 w-5 border-2 border-gray-300 border-t-primary rounded-full" />
        ) : (
          <GoogleIcon />
        )}
        {t('auth.signUpWithGoogle', 'Sign up with Google')}
      </Button>

      {/* Facebook Sign Up Button */}
      {showFacebookLogin && (
        <Button
          type="button"
          variant="outline"
          className="w-full h-12 text-base font-medium"
          onClick={handleFacebookLogin}
          disabled={socialLoading !== ''}
        >
          {socialLoading === 'facebook' ? (
            <div className="animate-spin mr-2 h-5 w-5 border-2 border-gray-300 border-t-[#1877F2] rounded-full" />
          ) : (
            <FacebookIcon />
          )}
          {t('auth.signUpWithFacebook', 'Sign up with Facebook')}
        </Button>
      )}

      <OrDivider text={t('auth.orContinueWith', 'or fill out the form')} />

      {/* Email Sign Up Form */}
      <form onSubmit={handleSignUp}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role">{t('auth.accountType')}</Label>
            <Select value={signUpData.role} onValueChange={(value) => {
                setSignUpData({ ...signUpData, role: value });
                if (value !== 'job_seeker') { setReferralCodeInput(''); setReferralCodeValid(null); setReferrerName(''); }
              }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="job_seeker">
                  <div className="flex items-center gap-2">
                    <User size={16} />
                    {t('auth.jobSeeker')}
                  </div>
                </SelectItem>
                <SelectItem value="employer">
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} />
                    {t('auth.employer')}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Full name only for job seekers — employers use business name instead */}
          {signUpData.role === 'job_seeker' && (
            <>
              {/* Resume upload — anonymous parse, pre-fills the form below */}
              <AnonymousResumeUploadCard
                onFileSelected={(file) => setPendingResumeFile(file)}
                onParsed={(extracted) => {
                  console.log('[Resume parse] Claude returned:', extracted);
                  // Hold full extracted data so post-signup we can apply fields
                  // the signup form doesn't capture (education_field, tagline, work_history)
                  setPendingResumeExtracted(extracted);
                  // Map parsed fields onto signup form. Only fields the form actually
                  // captures. The form is editable, so the user can fix anything wrong.
                  setSignUpData((prev) => {
                    const next = { ...prev };
                    if (extracted.name) next.name = extracted.name;
                    if (extracted.email && !prev.email) next.email = extracted.email;  // pre-fill email if user hasn't typed one yet
                    if (extracted.phone) next.phone = extracted.phone;
                    if (extracted.town && BOHOL_TOWNS.includes(extracted.town)) next.town = extracted.town;
                    if (extracted.years_of_experience) next.years_of_experience = extracted.years_of_experience;
                    if (extracted.education_level) next.education_level = extracted.education_level;
                    if (Array.isArray(extracted.skills) && extracted.skills.length > 0) {
                      next.skills = extracted.skills.slice(0, 12);
                    }
                    console.log('[Resume parse] Pre-filled signup form fields:',
                      Object.keys(next).filter(k => prev[k] !== next[k]));
                    return next;
                  });
                }}
              />

              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.fullName')}</Label>
                <Input
                  id="name"
                  placeholder="Juan Dela Cruz"
                  value={signUpData.name}
                  onChange={(e) => setSignUpData({ ...signUpData, name: e.target.value })}
                  required
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={signUpData.email}
              onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
              autoComplete="email"
              inputMode="email"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="town">{t('profile.location') || 'Location'}</Label>
            <Select value={signUpData.town} onValueChange={(value) => setSignUpData({ ...signUpData, town: value })} required>
              <SelectTrigger>
                <SelectValue placeholder={t('post.selectLocation')} />
              </SelectTrigger>
              <SelectContent>
                {BOHOL_TOWNS.map((town) => (
                  <SelectItem key={town} value={town}>{town}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Job seeker-specific fields */}
          {signUpData.role === 'job_seeker' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('profile.phone') || 'Phone Number'} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="09XX XXX XXXX"
                  value={signUpData.phone}
                  onChange={(e) => setSignUpData({ ...signUpData, phone: e.target.value })}
                  autoComplete="tel"
                  inputMode="tel"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="years_of_experience">{t('profile.yearsOfExperience') || 'Years of Experience'} *</Label>
                <Select value={signUpData.years_of_experience} onValueChange={(value) => setSignUpData({ ...signUpData, years_of_experience: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder={t('profile.selectExperience') || 'Select experience level'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="less_than_1">{t('profile.lessThan1Year') || 'Less than 1 year'}</SelectItem>
                    <SelectItem value="1_to_3">{t('profile.1to3Years') || '1-3 years'}</SelectItem>
                    <SelectItem value="3_to_5">{t('profile.3to5Years') || '3-5 years'}</SelectItem>
                    <SelectItem value="5_to_10">{t('profile.5to10Years') || '5-10 years'}</SelectItem>
                    <SelectItem value="10_plus">{t('profile.10PlusYears') || '10+ years'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="education_level">{t('profile.educationLevel') || 'Education Level'} *</Label>
                <Select value={signUpData.education_level} onValueChange={(value) => setSignUpData({ ...signUpData, education_level: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder={t('profile.selectEducation') || 'Select education level'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="elementary">{t('profile.elementary') || 'Elementary'}</SelectItem>
                    <SelectItem value="high_school">{t('profile.highSchool') || 'High School'}</SelectItem>
                    <SelectItem value="vocational">{t('profile.vocational') || 'Vocational'}</SelectItem>
                    <SelectItem value="college_level">{t('profile.collegeLevel') || 'College Level'}</SelectItem>
                    <SelectItem value="college_graduate">{t('profile.collegeGraduate') || 'College Graduate'}</SelectItem>
                    <SelectItem value="bachelors">{t('profile.bachelors') || "Bachelor's Degree"}</SelectItem>
                    <SelectItem value="masters">{t('profile.masters') || "Post Graduate"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_of_birth">{t('profile.dateOfBirth') || 'Date of Birth'} *</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={signUpData.date_of_birth}
                  onChange={(e) => setSignUpData({ ...signUpData, date_of_birth: e.target.value })}
                  min="1925-01-01"
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                  required
                />
                <p className="text-xs text-muted-foreground">You must be at least 16 years old to register</p>
              </div>

              {/* Skills Selection */}
              <div className="space-y-2">
                <Label>{t('profile.skills', 'Your Skills')} *</Label>
                <p className="text-xs text-muted-foreground">{t('auth.selectSkillsHint', 'Select skills that match your experience (at least 1)')}</p>
                <SkillsSelector
                  options={WORKER_SKILLS}
                  value={signUpData.skills}
                  onChange={(skills) => setSignUpData({ ...signUpData, skills })}
                  max={12}
                />
              </div>

              {/* Referral Code (optional) */}
              <div className="space-y-2">
                <Label htmlFor="referral_code">Referral Code (optional)</Label>
                <div className="relative">
                  <Input
                    id="referral_code"
                    type="text"
                    placeholder="e.g., MARIA2026"
                    value={referralCodeInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      setReferralCodeInput(val);
                      setReferralCodeValid(null);
                      setReferrerName('');
                    }}
                    onBlur={() => {
                      const code = referralCodeInput.trim();
                      if (code.length >= 4) {
                        api.get(`/api/referrals/validate/${encodeURIComponent(code)}`)
                          .then(res => {
                            setReferralCodeValid(res.data?.valid || false);
                            if (res.data?.valid) {
                              setReferrerName(res.data.referrer_name || '');
                              setReferralCode(code);
                              try { localStorage.setItem('referral_code', code); } catch {}
                            }
                          })
                          .catch(() => setReferralCodeValid(false));
                      } else if (code.length > 0) {
                        setReferralCodeValid(false);
                      }
                    }}
                    maxLength={20}
                    className={referralCodeValid === true ? 'border-green-500 pr-10' : referralCodeValid === false ? 'border-red-400 pr-10' : ''}
                  />
                  {referralCodeValid === true && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-sm font-medium">
                      ✓
                    </span>
                  )}
                  {referralCodeValid === false && referralCodeInput.length > 0 && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 text-sm">
                      Not found
                    </span>
                  )}
                </div>
                {referralCodeValid === true && referrerName && (
                  <p className="text-sm text-green-600">Referred by {referrerName}</p>
                )}
              </div>
            </>
          )}

          {/* Employer-specific fields */}
          {signUpData.role === 'employer' && (
            <>
              {/* Employer type selection */}
              <div className="space-y-2">
                <Label>{t('auth.employerType', 'What type of employer are you?')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSignUpData({ ...signUpData, is_private_household: false })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      !signUpData.is_private_household
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    }`}
                  >
                    <Briefcase size={20} />
                    <span className="text-sm font-medium">{t('auth.business', 'Business')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignUpData({ ...signUpData, is_private_household: true, business_name: '', industry: '', business_type: '', employee_count: '', business_description: '' })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      signUpData.is_private_household
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    }`}
                  >
                    <Home size={20} />
                    <span className="text-sm font-medium">{t('auth.privateHousehold', 'Private Household')}</span>
                  </button>
                </div>
              </div>

              {signUpData.is_private_household ? (
                /* Private household — minimal fields */
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-sm text-muted-foreground">
                    {t('auth.privateHouseholdNote', 'Your listing will appear as "Private Household in [your municipality]". Perfect for hiring nannies, kasambahay, drivers, caretakers, etc.')}
                  </p>
                </div>
              ) : (
                /* Business employer — full fields */
                <>
                  <div className="space-y-2">
                    <Label htmlFor="business_name">{t('auth.businessName')} *</Label>
                    <Input
                      id="business_name"
                      placeholder="ABC Company Inc."
                      value={signUpData.business_name}
                      onChange={(e) => setSignUpData({ ...signUpData, business_name: e.target.value })}
                      required
                    />
                    <p className="text-xs text-muted-foreground">{t('auth.businessNameNote', 'This is the name shown publicly to job seekers.')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">{t('auth.industry')} *</Label>
                    <Select value={signUpData.industry} onValueChange={(value) => setSignUpData({ ...signUpData, industry: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('auth.selectIndustry')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Agriculture">Agriculture</SelectItem>
                        <SelectItem value="Construction">Construction</SelectItem>
                        <SelectItem value="Education">Education</SelectItem>
                        <SelectItem value="Food & Beverage">Food & Beverage</SelectItem>
                        <SelectItem value="Healthcare">Healthcare</SelectItem>
                        <SelectItem value="Hospitality & Tourism">Hospitality & Tourism</SelectItem>
                        <SelectItem value="IT & Technology">IT & Technology</SelectItem>
                        <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="Retail">Retail</SelectItem>
                        <SelectItem value="Transportation">Transportation</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="business_type">{t('auth.businessType')}</Label>
                    <Select value={signUpData.business_type} onValueChange={(value) => setSignUpData({ ...signUpData, business_type: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('auth.selectBusinessType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                        <SelectItem value="Partnership">Partnership</SelectItem>
                        <SelectItem value="Corporation">Corporation</SelectItem>
                        <SelectItem value="Cooperative">Cooperative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="employee_count">{t('auth.numberOfEmployees')} *</Label>
                    <Select value={signUpData.employee_count} onValueChange={(value) => setSignUpData({ ...signUpData, employee_count: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('auth.selectEmployeeCount')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-5">1-5 employees</SelectItem>
                        <SelectItem value="6-10">6-10 employees</SelectItem>
                        <SelectItem value="11-50">11-50 employees</SelectItem>
                        <SelectItem value="51-100">51-100 employees</SelectItem>
                        <SelectItem value="101-500">101-500 employees</SelectItem>
                        <SelectItem value="500+">500+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="business_description">{t('auth.businessDescription')}</Label>
                    <Input
                      id="business_description"
                      placeholder={t('auth.businessDescriptionPlaceholder')}
                      value={signUpData.business_description}
                      onChange={(e) => setSignUpData({ ...signUpData, business_description: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="business_phone">{t('auth.businessPhone', 'Phone Number')} *</Label>
                <Input
                  id="business_phone"
                  type="tel"
                  placeholder="09XX XXX XXXX"
                  value={signUpData.business_phone}
                  onChange={(e) => setSignUpData({ ...signUpData, business_phone: e.target.value })}
                  autoComplete="tel"
                  inputMode="tel"
                  required
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.createPassword')}</Label>
            <Input
              id="password"
              type="password"
              value={signUpData.password}
              onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
              autoComplete="new-password"
              required
            />
            <p className="text-xs text-muted-foreground">{t('auth.createPasswordHint')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
            <Input
              id="confirm-password"
              type="password"
              value={signUpData.confirmPassword}
              onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
              autoComplete="new-password"
              required
            />
          </div>

          {/* Privacy Policy and Terms Agreement - Required before signup */}
          <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Checkbox
              id="terms-agreement"
              checked={termsAgreed}
              onCheckedChange={(checked) => setTermsAgreed(checked)}
              className="mt-0.5"
            />
            <label htmlFor="terms-agreement" className="text-sm leading-relaxed cursor-pointer">
              <span className="text-blue-900 dark:text-blue-100">
                {t('auth.agreeToTerms', 'I have read and agree to the')}{' '}
              </span>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                {t('footer.privacy', 'Privacy Policy')}
              </a>{' '}
              <span className="text-blue-900 dark:text-blue-100">{t('common.and', 'and')}</span>{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                {t('footer.terms', 'Terms of Service')}
              </a>
            </label>
          </div>

          <Button type="submit" className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700" disabled={loading || !termsAgreed}>
            {loading ? t('common.loading') : t('auth.createAccount', 'Sign Up')}
          </Button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 border-2 border-primary/20">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-primary">Language / Lengguwahe</span>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 object-contain" />
          </div>
          <CardTitle className="text-2xl text-center">{t('app.name')}</CardTitle>
          <CardDescription className="text-center">
            {showSignUpForm
              ? (t('auth.createAccountDescription') || 'Create your account to get started')
              : t('app.tagline')
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-0">
          {showSignUpForm ? renderSignUpForm() : renderLoginForm()}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
